import React, { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import '../styles/PhotoModal.css'; // Import the new CSS file
import AgentContactModal from './AgentContactModal';
import { useModalClose } from '../hooks/useModalClose';
import { getModalImageUrl, getOptimizedImageUrl } from '../utils/imageUtils';
import { getAddressParts } from '../utils/addressUtils';

const PhotoModal = ({ listing, onClose, isMobile, onAgentClick, onHostClick }) => {
  // Renamed photoIndex to currentIndex, which refers to displayPhotos array
  const [currentIndex, setCurrentIndex] = useState(0);
  const [photos, setPhotos] = useState([]); // Actual filtered photos
  const [displayPhotos, setDisplayPhotos] = useState([]); // Photos with cloned ends for infinite loop
  const [isTransitionEnabled, setIsTransitionEnabled] = useState(true);
  
  // Smooth close handler with animation
  const { isClosing, handleClose } = useModalClose(onClose, 200);

  const [slideWidth, setSlideWidth] = useState(0);
  const [dragOffset, setDragOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [isPerformingSeamJump, setIsPerformingSeamJump] = useState(false);
  const [isLandscape, setIsLandscape] = useState(false);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth > 768);

  const photoStripContainerRef = useRef(null);
  const startXRef = useRef(0);
  const startTimeRef = useRef(0);
  const prefetchedUrlsRef = useRef(new Set());
  const modalOpenedAtRef = useRef(null);
  const firstImageReportedRef = useRef(false);

  const isModalMetricsEnabled = useCallback(() => {
    // Dev-only by default; can be enabled in production by setting localStorage:
    // localStorage.setItem('rmkp_modal_metrics', '1')
    try {
      if (import.meta.env.DEV) return true;
      return window.localStorage.getItem('rmkp_modal_metrics') === '1';
    } catch {
      return false;
    }
  }, []);

  // Create a key based on media URLs for more robust change detection
  const mediaKey = React.useMemo(() => 
    (listing?.raw?.Media && Array.isArray(listing.raw.Media)) 
      ? listing.raw.Media.map(m => m?.MediaURL || '').join(',') 
      : '', 
  [listing?.raw?.Media]);

  const getPrefetchUrlForModal = useCallback((originalUrl) => {
    // Choose a practical size for prefetch based on viewport + DPR.
    // We keep the 4:3 aspect ratio used by modal resizing (e.g. 2400x1800).
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const viewport = Math.max(window.innerWidth || 0, window.innerHeight || 0);
    const targetW = Math.round(viewport * dpr);

    // Bucket to known CDN sizes to maximize cache reuse.
    const width = targetW >= 1800 ? 2400 : (targetW >= 1300 ? 1600 : 1200);
    const height = Math.round(width * 0.75);
    return getOptimizedImageUrl(originalUrl, width, height);
  }, []);

  // Effect to update photos and photoIndex when listing changes
  useEffect(() => {
    // Reset prefetch bookkeeping when listing/media changes
    prefetchedUrlsRef.current = new Set();
    modalOpenedAtRef.current = performance.now();
    firstImageReportedRef.current = false;

    if (listing?.raw?.Media && Array.isArray(listing.raw.Media)) {
      const actualPhotos = listing.raw.Media.filter(m => m && m.MediaURL && m.Permission && Array.isArray(m.Permission) && m.Permission.includes('Public') && m.MediaCategory === 'Photo');
      
      setIsTransitionEnabled(false); // Disable transitions before setting photos and index
      setPhotos(actualPhotos);

      if (actualPhotos.length > 1) {
        const firstPhotoClone = { ...actualPhotos[0], id: `${actualPhotos[0].MediaKey || 'photo-0'}-clone-end` };
        const lastPhotoClone = { ...actualPhotos[actualPhotos.length - 1], id: `${actualPhotos[actualPhotos.length - 1].MediaKey || `photo-${actualPhotos.length - 1}`}-clone-start` };
        setDisplayPhotos([lastPhotoClone, ...actualPhotos, firstPhotoClone]);
        setCurrentIndex(1); // Start at the first actual photo
      } else {
        setDisplayPhotos(actualPhotos);
        setCurrentIndex(0);
      }

    } else {
      setIsTransitionEnabled(false); // Also handle for empty case
      setPhotos([]);
      setDisplayPhotos([]);
      setCurrentIndex(0);
      // Transitions will be re-enabled by useLayoutEffect if displayPhotos.length changes
    }
  }, [listing, mediaKey]);

  const reportModalMetric = useCallback((payload) => {
    if (!isModalMetricsEnabled()) return;
    // eslint-disable-next-line no-console
    console.log('[PhotoModal metrics]', payload);
  }, [isModalMetricsEnabled]);

  const getResourceTimingForUrl = useCallback((url) => {
    try {
      const entries = performance.getEntriesByType('resource');
      // Find the last matching entry (covers retries/redirects)
      for (let i = entries.length - 1; i >= 0; i--) {
        const e = entries[i];
        if (e && e.name === url) return e;
      }
      return null;
    } catch {
      return null;
    }
  }, []);

  const handleActiveImageLoad = useCallback((e, { slideIndex }) => {
    if (!isModalMetricsEnabled()) return;
    if (firstImageReportedRef.current) return;
    if (slideIndex !== currentIndex) return;

    firstImageReportedRef.current = true;
    const now = performance.now();
    const openedAt = modalOpenedAtRef.current ?? now;
    const msToFirstImage = Math.max(0, now - openedAt);
    const currentSrc = e?.currentTarget?.currentSrc || e?.currentTarget?.src || null;
    const wasPrefetched = currentSrc ? prefetchedUrlsRef.current.has(currentSrc) : false;
    const timing = currentSrc ? getResourceTimingForUrl(currentSrc) : null;

    reportModalMetric({
      listingKey: listing?.listingKey || listing?.raw?.ListingKey || null,
      msToFirstImage: Math.round(msToFirstImage),
      currentSrc,
      wasPrefetched,
      // These fields depend on Timing-Allow-Origin from CDN; best-effort only.
      transferSize: timing?.transferSize ?? null,
      encodedBodySize: timing?.encodedBodySize ?? null,
      decodedBodySize: timing?.decodedBodySize ?? null,
    });
  }, [currentIndex, getResourceTimingForUrl, isModalMetricsEnabled, listing, reportModalMetric]);

  // Prefetch adjacent images (next/prev) for best perceived performance.
  useEffect(() => {
    if (!displayPhotos || displayPhotos.length === 0) return;
    if (!Number.isFinite(currentIndex)) return;

    // Prefetch depth is tunable via localStorage (for measurement/tuning):
    // - rmkp_prefetch_depth_mobile: default 1
    // - rmkp_prefetch_depth_desktop: default 2
    const getDepth = (key, fallback) => {
      try {
        const raw = window.localStorage.getItem(key);
        const n = raw == null ? fallback : Number(raw);
        if (!Number.isFinite(n)) return fallback;
        return Math.max(0, Math.min(6, Math.floor(n)));
      } catch {
        return fallback;
      }
    };

    const depth = isDesktop ? getDepth('rmkp_prefetch_depth_desktop', 2) : getDepth('rmkp_prefetch_depth_mobile', 1);
    const indicesToPrefetch = new Set();
    for (let d = 1; d <= depth; d++) {
      indicesToPrefetch.add(currentIndex - d);
      indicesToPrefetch.add(currentIndex + d);
    }

    for (const idx of indicesToPrefetch) {
      if (idx < 0 || idx >= displayPhotos.length) continue;
      const url = displayPhotos[idx]?.MediaURL;
      if (!url || typeof url !== 'string') continue;

      const prefetchUrl = getPrefetchUrlForModal(url);
      if (!prefetchUrl || typeof prefetchUrl !== 'string') continue;
      if (prefetchedUrlsRef.current.has(prefetchUrl)) continue;

      prefetchedUrlsRef.current.add(prefetchUrl);
      const img = new Image();
      img.decoding = 'async';
      img.src = prefetchUrl;
    }
  }, [currentIndex, displayPhotos, isDesktop, getPrefetchUrlForModal]);

  // Effect to calculate slide width (width of the container) using ResizeObserver
  useLayoutEffect(() => {
    const container = photoStripContainerRef.current;
    if (!container) return;

    const resizeObserver = new ResizeObserver(entries => {
      for (let entry of entries) {
        setSlideWidth(entry.contentRect.width);
      }
    });

    resizeObserver.observe(container);
    const initialWidth = container.offsetWidth;
    setSlideWidth(initialWidth);

    // slideWidth is set. Transitions will be enabled by a separate useEffect
    // when all initial layout conditions are met.

    return () => {
      resizeObserver.unobserve(container);
      resizeObserver.disconnect();
    };
  }, [displayPhotos.length]); // Key dependency

  // Effect to enable transitions once the initial layout is stable
  useEffect(() => {
    const initialCurrentIndex = photos.length > 1 ? 1 : 0;
    if (
      slideWidth > 0 &&
      displayPhotos.length > 0 &&
      currentIndex === initialCurrentIndex &&
      !isTransitionEnabled // Only act if transitions are currently off
    ) {
      // All conditions for a stable initial layout are met
      requestAnimationFrame(() => {
        setIsTransitionEnabled(true);
      });
    }
  }, [slideWidth, displayPhotos.length, currentIndex, photos.length, isTransitionEnabled, setIsTransitionEnabled]);

  // Effect to detect orientation changes on mobile
  useEffect(() => {
    if (!isMobile) {
      setIsLandscape(false); // Ensure landscape is false if not mobile
      return;
    }

    const mediaQuery = window.matchMedia('(orientation: landscape)');
    const handleOrientationChange = () => setIsLandscape(mediaQuery.matches);

    // Initial check
    handleOrientationChange();

    mediaQuery.addEventListener('change', handleOrientationChange);

    return () => {
      mediaQuery.removeEventListener('change', handleOrientationChange);
    };
  }, [isMobile]);

  // Effect to detect desktop vs mobile
  useEffect(() => {
    const mediaQuery = window.matchMedia('(min-width: 769px)');
    const handleResize = () => setIsDesktop(mediaQuery.matches);

    // Initial check
    handleResize();

    mediaQuery.addEventListener('change', handleResize);

    return () => {
      mediaQuery.removeEventListener('change', handleResize);
    };
  }, []);

  // Effect for handling infinite loop seam jump
  useEffect(() => {
    // Check if we are on a cloned slide and need to jump
    // photos.length is the count of actual photos
    if (photos.length <= 1) return; // No jump needed for 0 or 1 actual photo

    if (currentIndex === 0) { // On the cloned last slide (at the beginning)
      if (isDesktop) {
        // On desktop, jump immediately to avoid flash (clones are identical to real photos)
        setCurrentIndex(photos.length);
      } else {
        // On mobile, wait for slide animation to complete
        const timer = setTimeout(() => {
          setIsPerformingSeamJump(true);
          setIsTransitionEnabled(false);
          setCurrentIndex(photos.length);
          setTimeout(() => {
            requestAnimationFrame(() => {
              setIsTransitionEnabled(true);
              setIsPerformingSeamJump(false);
            });
          }, 0);
        }, 350);
        return () => clearTimeout(timer);
      }
    } else if (currentIndex === displayPhotos.length - 1) { // On the cloned first slide (at the end)
      if (isDesktop) {
        // On desktop, jump immediately to avoid flash (clones are identical to real photos)
        setCurrentIndex(1);
      } else {
        // On mobile, wait for slide animation to complete
        const timer = setTimeout(() => {
          setIsPerformingSeamJump(true);
          setIsTransitionEnabled(false);
          setCurrentIndex(1);
          setTimeout(() => {
            requestAnimationFrame(() => {
              setIsTransitionEnabled(true);
              setIsPerformingSeamJump(false);
            });
          }, 0);
        }, 350);
        return () => clearTimeout(timer);
      }
    }
  }, [currentIndex, photos.length, displayPhotos.length, setCurrentIndex, setIsPerformingSeamJump, isDesktop]);

  // Effect for keyboard listeners and body overflow
  const handlePrevPhoto = useCallback(() => {
    // On desktop, allow navigation during seam jump since we keep transitions enabled
    if ((!isDesktop && isPerformingSeamJump) || photos.length <= 1) return;

    setCurrentIndex(prevIndex => {
      const newIndex = prevIndex - 1;
      // If moving to the cloned slide at the beginning (index 0)
      if (newIndex === 0 && !isDesktop) {
        setIsPerformingSeamJump(true); // Lock immediately on mobile only
      }
      return newIndex;
    });
  }, [isPerformingSeamJump, photos.length, setIsPerformingSeamJump, isDesktop]);

  const handleNextPhoto = useCallback(() => {
    // On desktop, allow navigation during seam jump since we keep transitions enabled
    if ((!isDesktop && isPerformingSeamJump) || photos.length <= 1) return;

    setCurrentIndex(prevIndex => {
      const newIndex = prevIndex + 1;
      // If moving to the cloned slide at the end
      // displayPhotos.length includes 2 clones, so actual photos.length + 2
      // The last index is displayPhotos.length - 1
      if (newIndex === displayPhotos.length - 1 && !isDesktop) {
        setIsPerformingSeamJump(true); // Lock immediately on mobile only
      }
      return newIndex;
    });
  }, [isPerformingSeamJump, photos.length, displayPhotos.length, setIsPerformingSeamJump, isDesktop]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        handleClose();
      } else if (e.key === 'ArrowLeft') {
                handlePrevPhoto();
      } else if (e.key === 'ArrowRight') {
        handleNextPhoto();
      }
    };
    if (photos.length > 0) {
      window.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';

      return () => {
        window.removeEventListener('keydown', handleKeyDown);
        document.body.style.overflow = 'auto';
      };
    } else {
      // Ensure body overflow is reset if modal closes before photos load (e.g. quick escape)
      document.body.style.overflow = 'auto';
    }
  }, [onClose, handleClose, handlePrevPhoto, handleNextPhoto, displayPhotos.length, photos.length]); // Added photos.length for the if (photos.length > 0) check

  // Return null if no listing or no photos to display
  if (!listing || !displayPhotos || displayPhotos.length === 0) return null;

  const handleTouchStart = (e) => {
    if (displayPhotos.length <= 1) return; // Or actual photos.length <=1 if clones aren't added for single photo

    startXRef.current = e.touches[0].clientX;
    startTimeRef.current = Date.now();
    setIsDragging(true);
  };

  const handleTouchMove = (e) => {
    if (!isDragging || photos.length <= 1) return; // Check against actual photos count
    const currentX = e.touches[0].clientX;
    let diff = currentX - startXRef.current;

    // Apply rubber band resistance at boundaries
    // Boundary check needs to consider actual photo indices if displayPhotos includes clones
    // For now, this might behave a bit off at extreme ends with clones, will refine with seam jump logic
    if ((currentIndex === 0 && diff > 0) || (currentIndex === displayPhotos.length - 1 && diff < 0)) {
      diff *= 0.3; // Reduce movement by 70%
    }
    setDragOffset(diff);
  };

  const handleTouchEnd = () => {
    if (!isDragging || photos.length <= 1) return; // Check against actual photos count
    setIsDragging(false);

    const endTime = Date.now();
    const duration = endTime - startTimeRef.current;
    const velocity = duration > 0 ? Math.abs(dragOffset) / duration : 0;

    // Quick flick detection (Instagram-like)
    if (velocity > 0.4) { // Adjusted velocity threshold slightly
      if (dragOffset < 0) {
        handleNextPhoto();
      } else if (dragOffset > 0) {
        handlePrevPhoto();
      }
    } else {
      // Fall back to distance threshold for slow swipes
      const threshold = slideWidth / 4;
      if (dragOffset < -threshold) { // Next photo
        handleNextPhoto();
      } else if (dragOffset > threshold) { // Prev photo
        handlePrevPhoto();
      }
    }
    setDragOffset(0); // Reset drag offset for the snap animation
  };

  // Backdrop tap-to-close (mobile only) with swipe/brush protection
  const handleBackdropPointerUp = (e) => {
    if (!isMobile) return; // Only allow backdrop close on mobile

    // Determine if the click target is one of the allowed elements for closing
    const targetIsOverlay = e.target === e.currentTarget;
    const targetIsPhotoStripContainer = photoStripContainerRef.current && e.target === photoStripContainerRef.current;
    const targetIsPhotoSlide = e.target.classList && e.target.classList.contains('photo-slide');

    if (!(targetIsOverlay || targetIsPhotoStripContainer || targetIsPhotoSlide)) {
      return;
    }
    
    // Improved tap detection: ignore if user was swiping
    if (e.pointerType === 'touch') {
      // Only check if they were swiping (> 5px movement)
      // Removed duration check as it was too restrictive on mobile
      if (Math.abs(dragOffset) > 5) {
        return;
      }
    }
    
    // Prevent the current pointer event from propagating
    e.preventDefault();
    e.stopPropagation();
    
    // Comprehensive click-through prevention for all event types
    const eventTypes = ['click', 'mousedown', 'mouseup', 'touchstart', 'touchend', 'pointerdown', 'pointerup'];
    const captureEvent = (event) => {
      event.stopPropagation();
      event.preventDefault();
    };
    
    // Add listeners for all event types in capture phase
    eventTypes.forEach(type => {
      document.addEventListener(type, captureEvent, true);
    });

    // Remove all listeners after a delay
    setTimeout(() => {
      eventTypes.forEach(type => {
        document.removeEventListener(type, captureEvent, true);
      });
    }, 300); // Increased to 300ms to catch synthesized click events

    // Close the modal
    handleClose();
  };


  // Format address with improved parsing
  const addressParts = getAddressParts(listing);

  // Format price with fallbacks
  const price = listing.price || listing.raw?.ListPrice;
  const formattedPrice = price ? `$${price.toLocaleString()}` : '';

  // Format open house date/time
  const formatOpenHouseTime = (startTime, endTime) => {
    if (!startTime || !endTime) return 'Time TBD';
    
    const start = new Date(startTime);
    const end = new Date(endTime);
    
    // Format day of week and date
    const dayOfWeek = start.toLocaleDateString('en-US', { weekday: 'short' });
    const month = start.toLocaleDateString('en-US', { month: 'short' });
    const day = start.getDate();
    
    // Format times in compact format (10am-12pm instead of 10:00 AM-12:00 PM)
    const startHour = start.getHours();
    const endHour = end.getHours();
    const startMin = start.getMinutes();
    const endMin = end.getMinutes();
    
    // Convert to 12-hour format and remove leading zeros
    const formatHour = (hour) => {
      const h = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
      return h.toString();
    };
    
    // Get AM/PM suffix
    const getAmPm = (hour) => hour >= 12 ? 'pm' : 'am';
    
    // Format start time
    const startTimeStr = `${formatHour(startHour)}${startMin > 0 ? ':' + startMin.toString().padStart(2, '0') : ''}${getAmPm(startHour)}`;
    
    // Format end time
    const endTimeStr = `${formatHour(endHour)}${endMin > 0 ? ':' + endMin.toString().padStart(2, '0') : ''}${getAmPm(endHour)}`;
    
    return `${dayOfWeek} ${month} ${day} • ${startTimeStr}-${endTimeStr}`;
  };

  // Get open house times (check for multiple times)
  const getOpenHouseTimes = () => {
    if (!listing.openHouseStartTime || !listing.openHouseEndTime) {
      return null;
    }
    
    // For now, we'll handle single open house time
    // If there are multiple times, they would be in separate open house records
    return formatOpenHouseTime(listing.openHouseStartTime, listing.openHouseEndTime);
  };

  const openHouseTime = getOpenHouseTimes();
  const listingStatus =
    listing?.status ||
    listing?.StandardStatus ||
    listing?.raw?.StandardStatus ||
    '';

  const isListingsMobileHeader = Boolean(isMobile && !openHouseTime);

  const getMobileListingDetailsLine = () => {
    // Land/Commercial: acreage only
    const propertyType = (listing?.propertyType || listing?.raw?.PropertyType || '').toString().toLowerCase();
    const isLandOrCommercial = propertyType.includes('land') || propertyType.includes('commercial');

    if (isLandOrCommercial) {
      const acresRaw = listing?.lotSizeAcres ?? listing?.raw?.LotSizeAcres;
      const n = Number(acresRaw);
      if (!Number.isFinite(n) || n <= 0) return '';
      const rounded = Math.round(n * 100) / 100;
      const valueText = rounded.toLocaleString(undefined, { maximumFractionDigits: 2 });
      const labelText = rounded === 1 ? 'acre' : 'acres';
      return `${valueText} ${labelText}`;
    }

    // Residential (and others): beds/baths/sqft where present
    const beds = listing?.beds ?? listing?.BedroomsTotal ?? listing?.raw?.BedroomsTotal;
    const baths = listing?.baths ?? listing?.BathroomsFull ?? listing?.raw?.BathroomsFull;
    const sqft = listing?.sqft ?? listing?.LivingArea ?? listing?.raw?.LivingArea ?? listing?.raw?.BuildingAreaTotal;

    const parts = [];
    if (beds != null && Number.isFinite(Number(beds)) && Number(beds) > 0) parts.push(`${Number(beds)} bd`);
    if (baths != null && Number.isFinite(Number(baths)) && Number(baths) > 0) parts.push(`${Number(baths)} ba`);
    if (sqft != null && Number.isFinite(Number(sqft)) && Number(sqft) > 0) parts.push(`${Number(sqft).toLocaleString()} sqft`);

    return parts.join(' • ');
  };

// Return the modal portal
return ReactDOM.createPortal(
  <div
    className={`photo-modal-overlay ${isMobile && isLandscape ? 'mobile-landscape' : ''} ${isClosing ? 'closing' : ''}`}
    style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 1)',
      zIndex: 1000,
    }}
    onPointerUp={handleBackdropPointerUp}
  >
    {/* Top Bar (Header) */}
    <div
      className="photo-modal-header"
      onClick={(e) => e.stopPropagation()} // Prevent click from bubbling to overlay
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        height: '60px',
        padding: '0 20px',
        backgroundColor: 'rgba(0,0,0,1)',
        color: 'white',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        zIndex: 1100,
        boxSizing: 'border-box',
      }}
    >
      {/* Top Left: Price, Beds, Baths - Hidden on mobile */}
      <div className="desktop-only" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', flex: 1 }}>
        {formattedPrice && (
          <div style={{
            fontSize: '16px',
            fontWeight: 'normal',
          }}>
            {formattedPrice}
          </div>
        )}
        <div style={{
          display: 'flex',
          gap: '8px',
          marginTop: formattedPrice ? '4px' : '0',
          fontSize: '13px',
          color: 'rgba(255,255,255,0.9)',
          whiteSpace: 'nowrap',
        }}>
          {(listing.beds || listing.BedroomsTotal) && <span>{listing.beds || listing.BedroomsTotal} {isMobile ? 'bd' : 'bed'}</span>}
          {(listing.beds || listing.BedroomsTotal) && (listing.baths || listing.BathroomsFull) ? <span style={{color: 'rgba(255,255,255,0.7)', margin: '0 0'}}>·</span> : ''}
          {(listing.baths || listing.BathroomsFull) && <span>{listing.baths || listing.BathroomsFull} {isMobile ? 'ba' : 'bath'}</span>}
          {(listing.baths || listing.BathroomsFull) && (listing.sqft || listing.LivingArea) ? <span style={{color: 'rgba(255,255,255,0.7)', margin: '0 0'}}>·</span> : ''}
          {(listing.sqft || listing.LivingArea) && <span>{(listing.sqft || listing.LivingArea).toLocaleString()} sqft</span>}
        </div>
      </div>
      
      {/* Center: Open House Date/Time and Host Agent */}
      <div style={{
        position: 'absolute',
        left: '50%',
        top: '50%',
        transform: 'translate(-50%, -50%)',
        textAlign: 'center',
        zIndex: 1
      }}>
        {openHouseTime ? (
          <div className="photo-modal-open-house-time" style={{
            fontSize: '16px',
            color: 'rgba(255,255,255,0.9)',
            fontWeight: 'bold',
            whiteSpace: 'nowrap',
            marginBottom: '2px'
          }}>
            {openHouseTime}
          </div>
        ) : (isListingsMobileHeader ? (
          <>
            {formattedPrice ? (
              <div className="photo-modal-open-house-time" style={{
                fontSize: '16px',
                color: 'rgba(255,255,255,0.9)',
                fontWeight: 'bold',
                whiteSpace: 'nowrap',
                marginBottom: '2px'
              }}>
                {formattedPrice}
              </div>
            ) : null}
            {(() => {
              const details = getMobileListingDetailsLine();
              if (!details) return null;
              return (
                <div style={{
                  fontSize: '13px',
                  color: 'rgba(255,255,255,0.8)',
                  whiteSpace: 'nowrap',
                  marginTop: '0px'
                }}>
                  {details}
                </div>
              );
            })()}
          </>
        ) : (listingStatus ? (
          <div className="photo-modal-open-house-time" style={{
            fontSize: '16px',
            color: 'rgba(255,255,255,0.9)',
            fontWeight: 'bold',
            whiteSpace: 'nowrap',
            marginBottom: '2px'
          }}>
            {listingStatus}
          </div>
        ) : null))}
        {listing?.hostDisplayName && (
          <div 
            style={{
              fontSize: '13px',
              color: 'rgba(255,255,255,0.8)',
              whiteSpace: 'nowrap',
              cursor: listing?.hostMemberKey ? 'pointer' : 'default',
              transition: 'color 0.2s ease'
            }}
            onClick={(e) => onHostClick && onHostClick(e, listing?.hostMemberKey)}
            onMouseOver={(e) => {
              if (listing?.hostMemberKey) {
                e.currentTarget.style.color = 'rgba(255,255,255,1)';
              }
            }}
            onMouseOut={(e) => {
              if (listing?.hostMemberKey) {
                e.currentTarget.style.color = 'rgba(255,255,255,0.8)';
              }
            }}
          >
            Host: {listing.hostDisplayName}
          </div>
        )}
      </div>
      
      {/* Top Right: Address - Hidden on mobile */}
      <div className="desktop-only" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', flex: 1, textAlign: 'right' }}>
        <div style={{
          fontSize: '16px',
          fontWeight: 'normal',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis'
        }}>
          {addressParts.streetAddress}
        </div>
        {addressParts.cityStateZip && (
          <div style={{
            fontSize: '13px',
            color: 'rgba(255,255,255,0.8)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            marginTop: '2px'
          }}>
            {addressParts.cityStateZip}
          </div>
        )}
      </div>
      
      {/* Close button - direct child of photo-modal-header */}
      <button
        className="photo-modal-close-button" 
        onClick={handleClose}
        style={{
          background: 'rgba(255, 255, 255, 0.1)',
          color: 'white',
          border: 'none',
          borderRadius: '4px',
          width: '36px',
          height: '36px',
          fontSize: '24px', // Increased from 20px for more prominent, uppercase-sized ×
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          outline: 'none',
          padding: 0,
          margin: 0,
          marginLeft: '20px',
          lineHeight: 1,
          flexShrink: 0
        }}
        onMouseOver={(e) => { e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)'; }}
        onMouseOut={(e) => { e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)'; }}
        onFocus={(e) => { e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)'; }}
        onBlur={(e) => { e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)'; }}
        aria-label="Close photo viewer"
      >
        ×
      </button>
    </div>

      {/* Main Image Display Area - Film Strip */}
      <div
        ref={photoStripContainerRef} // Add ref here
        className="photo-strip-container" // Styles for this will be in PhotoModal.css
        onTouchStart={photos.length > 1 ? handleTouchStart : undefined}
        onTouchMove={photos.length > 1 ? handleTouchMove : undefined}
        onTouchEnd={photos.length > 1 ? handleTouchEnd : undefined}
        style={{ // Dynamic cursor based on dragging state and photo count
          cursor: photos.length > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default',
        }}
      >
        {/* Navigation Arrows - Desktop Only */}
        {photos.length > 1 && (
          <>
            <button
              className="photo-modal-arrow prev-arrow desktop-only-arrow"
              onClick={handlePrevPhoto}
              aria-label="Previous photo"
            >
              {'<'}
            </button>
            <button
              className="photo-modal-arrow next-arrow desktop-only-arrow"
              onClick={handleNextPhoto}
              aria-label="Next photo"
            >
              {'>'}
            </button>
          </>
        )}

        <div
          className={`photo-strip ${isDragging ? 'dragging' : ''} ${!isTransitionEnabled ? 'no-transition' : ''}`}
          style={{
            transform: isDesktop ? 'none' : `translateX(${-currentIndex * slideWidth + dragOffset}px)`,
            width: isDesktop ? '100%' : (slideWidth > 0 && displayPhotos.length > 0 ? `${slideWidth * displayPhotos.length}px` : 'auto'),
            transition: isTransitionEnabled ? undefined : 'none', // Dynamically control transition
          }}
        >
          {displayPhotos.map((photo, index) => {
            // Preload strategy: load photos that will be visible in viewport
            // Desktop: 4 photos for instant fade transitions
            // Mobile: Need 3 photos for film strip swipe (photos are side-by-side in strip)
            const shouldLoad = isDesktop ? Math.abs(index - currentIndex) <= 4 : Math.abs(index - currentIndex) <= 3;
            const photoURL = photo?.MediaURL; 
            // Use the unique ID generated for clones, or MediaKey for actual photos
            const photoKey = photo?.id || photo?.MediaKey || `slide-${index}-${photoURL || ''}`;

            return (
              <div 
                className={`photo-slide ${isDesktop && index === currentIndex ? 'active' : ''}`} 
                key={photoKey} 
                style={{ width: isDesktop ? '100%' : (slideWidth > 0 ? `${slideWidth}px` : '100%') }}
              >
                {shouldLoad && photoURL ? (
                  <img
                    // Use responsive srcSet so mobile/desktop fetch the right size instead of always pulling 2400px.
                    src={getOptimizedImageUrl(photoURL, 1600, 1200)}
                    srcSet={[
                      `${getOptimizedImageUrl(photoURL, 1200, 900)} 1200w`,
                      `${getOptimizedImageUrl(photoURL, 1600, 1200)} 1600w`,
                      `${getModalImageUrl(photoURL)} 2400w`,
                    ].join(', ')}
                    sizes="100vw"
                    alt={`Photo ${index + 1}`}
                    className="photo-modal-image"
                    loading={Math.abs(index - currentIndex) <= 2 ? 'eager' : 'lazy'}
                    decoding="async"
                    fetchpriority={index === currentIndex ? 'high' : 'auto'}
                    onLoad={(e) => handleActiveImageLoad(e, { slideIndex: index })}
                  />
                ) : (
                  <div className="photo-placeholder" style={{ width: '100%', height: '100%' }} />
                )}
              </div>
            );
          })}
        </div>
        
      </div>

      {/* Bottom Bar (Footer) */}
      <div
        className="photo-modal-footer"
        onClick={(e) => e.stopPropagation()} // Prevent click from bubbling to overlay
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          height: '60px',
          padding: '0 20px',
          backgroundColor: 'rgba(0,0,0,1)',
          color: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          zIndex: 1100,
          boxSizing: 'border-box',
        }}
      >
        {/* Bottom Left: MLS Number - Hidden on mobile */}
        <div className="desktop-only" style={{
          fontSize: '16px',
          color: 'rgba(255,255,255,0.8)',
          whiteSpace: 'nowrap',
          minWidth: '70px',
          textAlign: 'left',
          flexShrink: 0,
          flex: 1
        }}>
          {listing?.listingId ? `MLS # ${listing.listingId}` : ''}
        </div>
        
        {/* Bottom Center: Photo Count and Agent Name */}
        {listing?.agentName && (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            flexShrink: 0,
            margin: '0 20px',
            flex: 1,
            justifyContent: 'center'
          }}>
            {/* Photo Count - Top (small style) */}
            {photos.length > 1 && (
              <div style={{
                fontSize: '13px',
                color: 'rgba(255,255,255,0.8)',
                whiteSpace: 'nowrap',
                marginBottom: '2px'
              }}>
                {(photos.length > 1 && currentIndex > 0 && currentIndex <= photos.length ? currentIndex -1 : 0) + 1} of {photos.length}
              </div>
            )}
            
            {/* Listing Agent - Bottom (big style) */}
            <div 
              style={{
                fontSize: '16px',
                color: 'rgba(255,255,255,0.9)',
                whiteSpace: 'nowrap',
                display: 'flex',
                alignItems: 'center',
                fontWeight: 'bold',
                cursor: listing.raw?.ListAgentKey ? 'pointer' : 'default',
                transition: 'color 0.2s ease'
              }}
              onClick={(e) => onAgentClick && onAgentClick(e, listing.raw?.ListAgentKey)}
              onMouseOver={(e) => {
                if (listing.raw?.ListAgentKey) {
                  e.currentTarget.style.color = 'rgba(255,255,255,1)';
                }
              }}
              onMouseOut={(e) => {
                if (listing.raw?.ListAgentKey) {
                  e.currentTarget.style.color = 'rgba(255,255,255,0.9)';
                }
              }}
            >
              <svg 
                width="16" 
                height="16" 
                viewBox="0 0 24 24" 
                fill="none" 
                xmlns="http://www.w3.org/2000/svg" 
                style={{
                  marginRight: '6px',
                  cursor: listing.raw?.ListAgentKey ? 'pointer' : 'default',
                  transition: 'opacity 0.2s ease'
                }}
                onClick={(e) => onAgentClick && onAgentClick(e, listing.raw?.ListAgentKey)}
                onMouseOver={(e) => {
                  if (listing.raw?.ListAgentKey) {
                    e.currentTarget.style.opacity = '0.7';
                  }
                }}
                onMouseOut={(e) => {
                  if (listing.raw?.ListAgentKey) {
                    e.currentTarget.style.opacity = '1';
                  }
                }}
              >
                <path d="M12 12C14.21 12 16 10.21 16 8C16 5.79 14.21 4 12 4C9.79 4 8 5.79 8 8C8 10.21 9.79 12 12 12ZM12 14C9.33 14 4 15.34 4 18V20H20V18C20 15.34 14.67 14 12 14Z" fill="rgba(255,255,255,0.9)"/>
              </svg>
              <span style={{whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>{listing.agentName}</span>
            </div>
          </div>
        )}
        
        {/* Bottom Right: Neighborhood - Hidden on mobile */}
        {(() => {
          // Get neighborhood or subdivision, fallback to city if not available
          const neighborhood = (() => {
            if (listing.raw?.SubdivisionName && listing.raw.SubdivisionName !== 'N/A' && listing.raw.SubdivisionName.trim() !== '') {
              return listing.raw.SubdivisionName;
            }
            return listing.city || listing.raw?.City || '';
          })();
          
          return neighborhood ? (
            <div className="desktop-only" style={{
              fontSize: '16px',
              color: 'rgba(255,255,255,0.9)',
              whiteSpace: 'nowrap',
              display: 'flex',
              alignItems: 'center',
              flexShrink: 0,
              flex: 1,
              justifyContent: 'flex-end'
            }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{marginRight: '6px'}}>
                <path d="M12 2C8.13 2 5 5.13 5 9C5 14.25 12 22 12 22C12 22 19 14.25 19 9C19 5.13 15.87 2 12 2ZM12 11.5C10.62 11.5 9.5 10.38 9.5 9C9.5 7.62 10.62 6.5 12 6.5C13.38 6.5 14.5 7.62 14.5 9C14.5 10.38 13.38 11.5 12 11.5Z" fill="rgba(255,255,255,0.9)"/>
              </svg>
              <span style={{whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>{neighborhood}</span>
            </div>
          ) : null;
        })()}
      </div>
    </div>,
    document.body
  );
};

export default PhotoModal;
